import React, { useContext } from 'react';
import { LanguageContext } from '../App';
import { SearchIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, DollarSignIcon, ArrowUpIcon, ArrowDownIcon } from 'lucide-react';
import { CountUp } from '../components/ui/CountUp';
import { useNotification } from '../hooks/useNotification';
export const Wallet: React.FC = () => {
  const {
    language
  } = useContext(LanguageContext);
  const {
    toast,
    notify
  } = useNotification();
  // Function to show transaction notifications
  const showTransactionNotification = (type: 'credit' | 'debit' | 'refund', amount: string, user: string) => {
    const titles = {
      credit: {
        en: 'New Credit Transaction',
        ar: 'معاملة إيداع جديدة'
      },
      debit: {
        en: 'New Debit Transaction',
        ar: 'معاملة سحب جديدة'
      },
      refund: {
        en: 'Refund Processed',
        ar: 'تمت معالجة الاسترداد'
      }
    };
    const messages = {
      credit: {
        en: `${amount} has been credited to ${user}'s wallet.`,
        ar: `تم إيداع ${amount} في محفظة ${user}.`
      },
      debit: {
        en: `${amount} has been debited from ${user}'s wallet.`,
        ar: `تم سحب ${amount} من محفظة ${user}.`
      },
      refund: {
        en: `${amount} has been refunded to ${user}'s wallet.`,
        ar: `تم إعادة ${amount} إلى محفظة ${user}.`
      }
    };
    const notificationTypes = {
      credit: 'success' as const,
      debit: 'info' as const,
      refund: 'warning' as const
    };
    notify({
      title: titles[type][language === 'en' ? 'en' : 'ar'],
      message: messages[type][language === 'en' ? 'en' : 'ar'],
      type: notificationTypes[type],
      actionText: language === 'en' ? 'View Details' : 'عرض التفاصيل',
      onAction: () => console.log(`View ${type} transaction details`)
    });
  };
  const transactions = [{
    id: 'TRX-8726',
    user: 'Ahmed Al-Sabah',
    userId: 'USR-5623',
    type: 'credit',
    amount: 'KWD 25.00',
    method: 'Credit Card',
    date: '2023-05-10 14:32',
    status: 'completed',
    description: 'Wallet Top-up'
  }, {
    id: 'TRX-8725',
    user: 'Fatima Al-Qadiri',
    userId: 'USR-5622',
    type: 'debit',
    amount: 'KWD 12.50',
    method: 'Wallet',
    date: '2023-05-10 13:45',
    status: 'completed',
    description: 'Order Payment (ORD-7828)'
  }, {
    id: 'TRX-8724',
    user: 'Mohammed Al-Mutairi',
    userId: 'USR-5621',
    type: 'credit',
    amount: 'KWD 50.00',
    method: 'Bank Transfer',
    date: '2023-05-10 11:20',
    status: 'completed',
    description: 'Wallet Top-up'
  }, {
    id: 'TRX-8723',
    user: 'Sarah Al-Enezi',
    userId: 'USR-5620',
    type: 'debit',
    amount: 'KWD 8.75',
    method: 'Wallet',
    date: '2023-05-10 10:15',
    status: 'completed',
    description: 'Order Payment (ORD-7826)'
  }, {
    id: 'TRX-8722',
    user: 'Abdullah Al-Rashidi',
    userId: 'USR-5619',
    type: 'refund',
    amount: 'KWD 15.25',
    method: 'Wallet',
    date: '2023-05-09 16:50',
    status: 'completed',
    description: 'Order Refund (ORD-7820)'
  }, {
    id: 'TRX-8721',
    user: 'Noura Al-Ali',
    userId: 'USR-5618',
    type: 'credit',
    amount: 'KWD 30.00',
    method: 'Credit Card',
    date: '2023-05-09 14:30',
    status: 'completed',
    description: 'Wallet Top-up'
  }, {
    id: 'TRX-8720',
    user: 'Khalid Al-Enezi',
    userId: 'USR-5617',
    type: 'debit',
    amount: 'KWD 10.50',
    method: 'Wallet',
    date: '2023-05-09 12:20',
    status: 'completed',
    description: 'Order Payment (ORD-7815)'
  }, {
    id: 'TRX-8719',
    user: 'Maryam Al-Sabah',
    userId: 'USR-5616',
    type: 'credit',
    amount: 'KWD 20.00',
    method: 'Credit Card',
    date: '2023-05-09 11:05',
    status: 'failed',
    description: 'Wallet Top-up (Failed)'
  }];
  const getTypeClass = (type: string) => {
    switch (type) {
      case 'credit':
        return 'bg-green-100 text-green-800';
      case 'debit':
        return 'bg-red-100 text-red-800';
      case 'refund':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const getTypeText = (type: string) => {
    if (language === 'en') {
      switch (type) {
        case 'credit':
          return 'Credit';
        case 'debit':
          return 'Debit';
        case 'refund':
          return 'Refund';
        default:
          return 'Unknown';
      }
    } else {
      switch (type) {
        case 'credit':
          return 'إيداع';
        case 'debit':
          return 'سحب';
        case 'refund':
          return 'استرداد';
        default:
          return 'غير معروف';
      }
    }
  };
  const getStatusClass = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const getStatusText = (status: string) => {
    if (language === 'en') {
      switch (status) {
        case 'completed':
          return 'Completed';
        case 'pending':
          return 'Pending';
        case 'failed':
          return 'Failed';
        default:
          return 'Unknown';
      }
    } else {
      switch (status) {
        case 'completed':
          return 'مكتمل';
        case 'pending':
          return 'قيد الانتظار';
        case 'failed':
          return 'فشل';
        default:
          return 'غير معروف';
      }
    }
  };
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'credit':
        return <ArrowUpIcon size={16} className="mr-1 text-green-500" data-id="element-1462" />;
      case 'debit':
        return <ArrowDownIcon size={16} className="mr-1 text-red-500" data-id="element-1463" />;
      case 'refund':
        return <ArrowUpIcon size={16} className="mr-1 text-blue-500" data-id="element-1464" />;
      default:
        return null;
    }
  };
  const handleTransactionClick = (transaction: any) => {
    showTransactionNotification(transaction.type as 'credit' | 'debit' | 'refund', transaction.amount, transaction.user);
  };
  return <div className="space-y-6" data-id="element-1465">
      <div className="flex justify-between items-center" data-id="element-1466">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-1467">
          {language === 'en' ? 'Wallet & Transactions' : 'المحفظة والمعاملات'}
        </h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1468">
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-1469">
          <div className="flex justify-between items-start" data-id="element-1470">
            <div data-id="element-1471">
              <p className="text-sm font-medium text-gray-600" data-id="element-1472">
                {language === 'en' ? 'Total Wallet Balance' : 'إجمالي رصيد المحفظة'}
              </p>
              <p className="mt-2 text-3xl font-bold text-gray-900" data-id="element-1473">
                KWD <CountUp to={24586.25} decimals={2} data-id="element-1474" />
              </p>
              <div className="mt-2 flex items-center" data-id="element-1475">
                <span className="text-sm font-medium text-green-600" data-id="element-1476">
                  +15.2%
                </span>
                <span className="text-sm text-gray-500 ml-1" data-id="element-1477">
                  {language === 'en' ? 'vs last month' : 'مقارنة بالشهر الماضي'}
                </span>
              </div>
            </div>
            <div className="p-3 rounded-full bg-congress-blue-50" data-id="element-1478">
              <DollarSignIcon size={24} className="text-congress-blue-600" data-id="element-1479" />
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-1480">
          <div className="flex justify-between items-start" data-id="element-1481">
            <div data-id="element-1482">
              <p className="text-sm font-medium text-gray-600" data-id="element-1483">
                {language === 'en' ? 'Total Credits' : 'إجمالي الإيداعات'}
              </p>
              <p className="mt-2 text-3xl font-bold text-gray-900" data-id="element-1484">
                KWD <CountUp to={18350} decimals={2} data-id="element-1485" />
              </p>
              <div className="mt-2 flex items-center" data-id="element-1486">
                <span className="text-sm font-medium text-green-600" data-id="element-1487">
                  +12.8%
                </span>
                <span className="text-sm text-gray-500 ml-1" data-id="element-1488">
                  {language === 'en' ? 'vs last month' : 'مقارنة بالشهر الماضي'}
                </span>
              </div>
            </div>
            <div className="p-3 rounded-full bg-green-50" data-id="element-1489">
              <ArrowUpIcon size={24} className="text-green-600" data-id="element-1490" />
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-1491">
          <div className="flex justify-between items-start" data-id="element-1492">
            <div data-id="element-1493">
              <p className="text-sm font-medium text-gray-600" data-id="element-1494">
                {language === 'en' ? 'Total Debits' : 'إجمالي السحوبات'}
              </p>
              <p className="mt-2 text-3xl font-bold text-gray-900" data-id="element-1495">
                KWD <CountUp to={12764.5} decimals={2} data-id="element-1496" />
              </p>
              <div className="mt-2 flex items-center" data-id="element-1497">
                <span className="text-sm font-medium text-red-600" data-id="element-1498">+8.5%</span>
                <span className="text-sm text-gray-500 ml-1" data-id="element-1499">
                  {language === 'en' ? 'vs last month' : 'مقارنة بالشهر الماضي'}
                </span>
              </div>
            </div>
            <div className="p-3 rounded-full bg-red-50" data-id="element-1500">
              <ArrowDownIcon size={24} className="text-red-600" data-id="element-1501" />
            </div>
          </div>
        </div>
      </div>
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-1502">
        <div className="px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0" data-id="element-1503">
          <h3 className="text-lg font-medium text-gray-900" data-id="element-1504">
            {language === 'en' ? 'Transaction History' : 'سجل المعاملات'}
          </h3>
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2" data-id="element-1505">
            <div className="relative" data-id="element-1506">
              <input type="text" placeholder={language === 'en' ? 'Search transactions...' : 'البحث عن معاملات...'} className="w-full sm:w-64 px-10 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-congress-blue-500 focus:border-congress-blue-500" data-id="element-1507" />
              <SearchIcon size={18} className="absolute left-3 top-2.5 text-gray-400" data-id="element-1508" />
            </div>
            <div className="relative" data-id="element-1509">
              <select className="appearance-none w-full px-4 py-2 pr-8 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-congress-blue-500 focus:border-congress-blue-500" data-id="element-1510">
                <option data-id="element-1511">
                  {language === 'en' ? 'All Types' : 'جميع الأنواع'}
                </option>
                <option data-id="element-1512">{language === 'en' ? 'Credit' : 'إيداع'}</option>
                <option data-id="element-1513">{language === 'en' ? 'Debit' : 'سحب'}</option>
                <option data-id="element-1514">{language === 'en' ? 'Refund' : 'استرداد'}</option>
              </select>
              <ChevronDownIcon size={16} className="absolute right-3 top-3 text-gray-400" data-id="element-1515" />
            </div>
            <div className="relative" data-id="element-1516">
              <select className="appearance-none w-full px-4 py-2 pr-8 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-congress-blue-500 focus:border-congress-blue-500" data-id="element-1517">
                <option data-id="element-1518">
                  {language === 'en' ? 'Last 7 days' : 'آخر 7 أيام'}
                </option>
                <option data-id="element-1519">
                  {language === 'en' ? 'Last 30 days' : 'آخر 30 يوم'}
                </option>
                <option data-id="element-1520">
                  {language === 'en' ? 'Last 90 days' : 'آخر 90 يوم'}
                </option>
                <option data-id="element-1521">
                  {language === 'en' ? 'Last year' : 'العام الماضي'}
                </option>
              </select>
              <ChevronDownIcon size={16} className="absolute right-3 top-3 text-gray-400" data-id="element-1522" />
            </div>
          </div>
        </div>
        <div className="overflow-x-auto" data-id="element-1523">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-1524">
            <thead className="bg-gray-50" data-id="element-1525">
              <tr data-id="element-1526">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1527">
                  {language === 'en' ? 'Transaction ID' : 'رقم المعاملة'}
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1528">
                  {language === 'en' ? 'User' : 'المستخدم'}
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1529">
                  {language === 'en' ? 'Type' : 'النوع'}
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1530">
                  {language === 'en' ? 'Amount' : 'المبلغ'}
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1531">
                  {language === 'en' ? 'Method' : 'الطريقة'}
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1532">
                  {language === 'en' ? 'Date & Time' : 'التاريخ والوقت'}
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1533">
                  {language === 'en' ? 'Status' : 'الحالة'}
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1534">
                  {language === 'en' ? 'Description' : 'الوصف'}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-1535">
              {transactions.map(transaction => <tr key={transaction.id} className="cursor-pointer hover:bg-gray-50" onClick={() => handleTransactionClick(transaction)} data-id="element-1536">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1537">
                    {transaction.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1538">
                    <div data-id="element-1539">{transaction.user}</div>
                    <div className="text-xs text-gray-400" data-id="element-1540">
                      {transaction.userId}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-1541">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getTypeClass(transaction.type)}`} data-id="element-1542">
                      <span className="flex items-center" data-id="element-1543">
                        {getTypeIcon(transaction.type)}
                        {getTypeText(transaction.type)}
                      </span>
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" data-id="element-1544">
                    <span className={transaction.type === 'debit' ? 'text-red-600' : transaction.type === 'credit' ? 'text-green-600' : 'text-blue-600'} data-id="element-1545">
                      {transaction.amount}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1546">
                    {transaction.method}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1547">
                    {transaction.date}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-1548">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusClass(transaction.status)}`} data-id="element-1549">
                      {getStatusText(transaction.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1550">
                    {transaction.description}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between" data-id="element-1551">
          <div className="text-sm text-gray-700" data-id="element-1552">
            {language === 'en' ? `Showing 1 to ${transactions.length} of ${transactions.length} results` : `عرض 1 إلى ${transactions.length} من ${transactions.length} نتيجة`}
          </div>
          <div className="flex space-x-2" data-id="element-1553">
            <button className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" data-id="element-1554">
              <ChevronLeftIcon size={16} className="mr-1" data-id="element-1555" />
              {language === 'en' ? 'Previous' : 'السابق'}
            </button>
            <button className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" data-id="element-1556">
              {language === 'en' ? 'Next' : 'التالي'}
              <ChevronRightIcon size={16} className="ml-1" data-id="element-1557" />
            </button>
          </div>
        </div>
      </div>
    </div>;
};